import React, { useEffect, useState } from "react";
import { Clock, AlertOctagon, Mail } from "lucide-react";
import { useAuth } from "../lib/auth";

const TRIAL_EXPIRED_MSG = (
  "Your 1 day trial is finished. Get in touch on "
  + "contact@capexara.com to continue your services."
);

function fmtRemaining(seconds) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0 h 0 m";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h} h ${m} m`;
}

/**
 * TrialBanner — surfaces the user's 24 h trial state at the top of
 * the dashboard. Shows:
 *   • "X hours left" countdown while the trial is active
 *   • The expired contact-line when the window has passed
 * Super-admins and trial-exempt users do not see the banner at all.
 *
 * Re-renders every 60 s so the countdown stays fresh without a page
 * reload.
 */
export default function TrialBanner() {
  const { user } = useAuth();
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(t);
  }, []);
  const trial = user?.trial;
  if (!trial || trial.exempt) return null;
  const expiresAt = trial.expires_at ? new Date(trial.expires_at) : null;
  const secondsLeft = expiresAt
    ? Math.max(0, Math.floor((expiresAt.getTime() - Date.now()) / 1000))
    : trial.seconds_left;
  const expired = trial.expired || (secondsLeft === 0 && trial.is_trial);
  if (!trial.is_trial && !expired) {
    return (
      <div data-testid="trial-banner-prelaunch"
            style={{ display: "flex", alignItems: "center", gap: 10,
                      padding: "8px 14px", marginBottom: 14, fontSize: 12,
                      background: "var(--pf-surface-2)",
                      color: "var(--pf-text)",
                      border: "1px solid var(--pf-border)",
                      borderRadius: 4 }}>
        <Clock size={14}/>
        Free 1-day trial — the 24 h window starts when you create
        your first project.
      </div>
    );
  }
  if (expired) {
    return (
      <div data-testid="trial-banner-expired"
            style={{ display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 14px", marginBottom: 14, fontSize: 13,
                      background: "rgba(220, 38, 38, 0.12)",
                      color: "#dc2626",
                      border: "1px solid #dc2626",
                      borderRadius: 4, fontWeight: 600 }}>
        <AlertOctagon size={16}/>
        <span>{TRIAL_EXPIRED_MSG}</span>
        <a href="mailto:contact@capexara.com"
            data-testid="trial-banner-contact"
            style={{ marginLeft: "auto", display: "inline-flex",
                      alignItems: "center", gap: 4, color: "#dc2626",
                      textDecoration: "underline", fontWeight: 600 }}>
          <Mail size={12}/> contact@capexara.com
        </a>
      </div>
    );
  }
  // Active trial — show countdown
  return (
    <div data-testid="trial-banner-active"
          style={{ display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 14px", marginBottom: 14, fontSize: 12,
                    background: "rgba(245, 158, 11, 0.10)",
                    color: "#92580a",
                    border: "1px solid #f59e0b",
                    borderRadius: 4 }}>
      <Clock size={14}/>
      <strong>Trial active</strong> · <span data-testid="trial-banner-remaining">
        {fmtRemaining(secondsLeft)} remaining
      </span>
      <span style={{ marginLeft: "auto", color: "var(--pf-text-muted)" }}>
        After this window read-only access continues; writes/runs are blocked.
        Need more? <a href="mailto:contact@capexara.com"
                       style={{ color: "var(--pf-spark)" }}>
          contact@capexara.com</a>
      </span>
    </div>
  );
}

export { TRIAL_EXPIRED_MSG };
